import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

export const Settings = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('general');

  const navItems = [
    { id: 'general', icon: 'fa-solid fa-gear', labelKey: 'general' },
    { id: 'profile', icon: 'fa-solid fa-user', labelKey: 'profile' },
    { id: 'security', icon: 'fa-solid fa-shield-halved', labelKey: 'security' },
    { id: 'notifications', icon: 'fa-solid fa-bell', labelKey: 'notifications' },
    { id: 'appearance', icon: 'fa-solid fa-palette', labelKey: 'appearance' },
    { id: 'api', icon: 'fa-solid fa-key', labelKey: 'apiKeys' },
    { id: 'billing', icon: 'fa-solid fa-credit-card', labelKey: 'billing' },
  ];

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">{t('settings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('manageAccount')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-2">
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left",
                      activeTab === item.id 
                        ? "bg-primary text-primary-foreground" 
                        : "hover:bg-muted text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <i className={cn(item.icon, "w-4")}></i>
                    {t(item.labelKey)}
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3 space-y-6">
          {activeTab === 'general' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('generalSettings')}</CardTitle>
                  <CardDescription>{t('configurePreferences')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('language')}</Label>
                      <Select defaultValue="en">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="de">Deutsch</SelectItem>
                          <SelectItem value="es">Español</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="zh">中文</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('currency')}</Label>
                      <Select defaultValue="usd">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usd">USD ($)</SelectItem>
                          <SelectItem value="eur">EUR (€)</SelectItem>
                          <SelectItem value="gbp">GBP (£)</SelectItem>
                          <SelectItem value="jpy">JPY (¥)</SelectItem>
                          <SelectItem value="btc">BTC (₿)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('timezone')}</Label>
                    <Select defaultValue="europe">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="utc">UTC</SelectItem>
                        <SelectItem value="europe">Europe/Berlin (GMT+1)</SelectItem>
                        <SelectItem value="eastern">US/Eastern (GMT-5)</SelectItem>
                        <SelectItem value="pacific">US/Pacific (GMT-8)</SelectItem>
                        <SelectItem value="asia">Asia/Tokyo (GMT+9)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div>
                      <p className="font-medium text-sm">{t('autoRefresh')}</p>
                      <p className="text-xs text-muted-foreground">{t('updatePrices')}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('profileSettings')}</CardTitle>
                <CardDescription>{t('updatePersonalInfo')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center">
                    <span className="text-2xl font-semibold text-secondary-foreground">TL</span>
                  </div>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm">
                      <i className="fa-solid fa-upload mr-2"></i>
                      {t('uploadPhoto')}
                    </Button>
                    <p className="text-xs text-muted-foreground">JPG, PNG or GIF. Max 2MB.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('firstName')}</Label>
                    <Input defaultValue="Tim" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('lastName')}</Label>
                    <Input defaultValue="Luczak" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('email')}</Label>
                  <Input type="email" defaultValue="info@tim-luczak.de" />
                </div>
                <div className="space-y-2">
                  <Label>{t('bio')}</Label>
                  <Input placeholder="Tell us about yourself..." />
                </div>
                <Button>
                  <i className="fa-solid fa-save mr-2"></i>
                  {t('saveChanges')}
                </Button>
              </CardContent>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('securitySettings')}</CardTitle>
                <CardDescription>{t('manageAccountSecurity')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <i className="fa-solid fa-lock text-primary"></i>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{t('password')}</p>
                        <p className="text-xs text-muted-foreground">{t('lastChanged')}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">{t('change')}</Button>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                        <i className="fa-solid fa-shield-check text-success"></i>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{t('twoFactor')}</p>
                        <p className="text-xs text-muted-foreground">{t('addExtraLayer')}</p>
                      </div>
                    </div>
                    <Badge variant="success">{t('enabled')}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                        <i className="fa-solid fa-fingerprint text-warning"></i>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{t('biometricLogin')}</p>
                        <p className="text-xs text-muted-foreground">{t('useFingerprintFace')}</p>
                      </div>
                    </div>
                    <Switch />
                  </div>
                </div>
                <div className="pt-4 border-t border-border">
                  <h4 className="font-medium text-sm mb-3">{t('activeSessions')}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <i className="fa-solid fa-laptop text-muted-foreground"></i>
                        <div>
                          <p className="text-sm">MacBook Pro - Chrome</p>
                          <p className="text-xs text-muted-foreground">Berlin, Germany • {t('currentSession')}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">{t('current')}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('notificationPreferences')}</CardTitle>
                <CardDescription>{t('chooseNotifications')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { icon: 'fa-solid fa-chart-line', titleKey: 'priceAlerts', descKey: 'getNotified', enabled: true },
                  { icon: 'fa-solid fa-arrow-right-arrow-left', titleKey: 'tradeConfirmations', descKey: 'receiveConfirmations', enabled: true },
                  { icon: 'fa-solid fa-newspaper', titleKey: 'marketNews', descKey: 'dailyDigest', enabled: false },
                  { icon: 'fa-solid fa-wallet', titleKey: 'portfolioUpdates', descKey: 'weeklyReports', enabled: true },
                  { icon: 'fa-solid fa-shield', titleKey: 'securityAlerts', descKey: 'accountSecurityNotifs', enabled: true },
                  { icon: 'fa-solid fa-bullhorn', titleKey: 'promotions', descKey: 'specialOffers', enabled: false },
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <i className={cn(item.icon, "text-primary text-sm")}></i>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{t(item.titleKey)}</p>
                        <p className="text-xs text-muted-foreground">{t(item.descKey)}</p>
                      </div>
                    </div>
                    <Switch defaultChecked={item.enabled} />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {activeTab === 'appearance' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('appearance')}</CardTitle>
                <CardDescription>{t('customizeLook')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label>{t('theme')}</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => theme === 'light' && toggleTheme()}
                      className={cn(
                        "p-4 rounded-lg border-2 transition-all",
                        theme === 'dark' ? "border-primary bg-primary/5" : "border-border"
                      )}
                    >
                      <div className="w-full h-16 rounded bg-slate-900 mb-2 flex items-center justify-center">
                        <i className="fa-solid fa-moon text-slate-400"></i>
                      </div>
                      <p className="text-sm font-medium">{t('dark')}</p>
                    </button>
                    <button
                      onClick={() => theme === 'dark' && toggleTheme()}
                      className={cn(
                        "p-4 rounded-lg border-2 transition-all",
                        theme === 'light' ? "border-primary bg-primary/5" : "border-border"
                      )}
                    >
                      <div className="w-full h-16 rounded bg-slate-100 mb-2 flex items-center justify-center border">
                        <i className="fa-solid fa-sun text-slate-600"></i>
                      </div>
                      <p className="text-sm font-medium">{t('light')}</p>
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <Label>{t('chartStyle')}</Label>
                  <Select defaultValue="area">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="area">{t('areaChart')}</SelectItem>
                      <SelectItem value="line">{t('lineChart')}</SelectItem>
                      <SelectItem value="candle">{t('candlestick')}</SelectItem>
                      <SelectItem value="bar">{t('barChart')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>{t('fontSize')}</Label>
                    <span className="text-sm text-muted-foreground">{t('medium')}</span>
                  </div>
                  <Slider defaultValue={[50]} max={100} step={25} />
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'api' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('apiKeys')}</CardTitle>
                <CardDescription>{t('manageApiKeys')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('tradingApiKey')}</p>
                      <p className="text-xs text-muted-foreground">{t('createdDaysAgo')}</p>
                    </div>
                    <Badge variant="success">{t('active')}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input value="sk_live_••••••••••••••••" readOnly className="font-mono text-sm" />
                    <Button variant="outline" size="icon">
                      <i className="fa-solid fa-copy"></i>
                    </Button>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  <i className="fa-solid fa-plus mr-2"></i>
                  {t('generateNewKey')}
                </Button>
              </CardContent>
            </Card>
          )}

          {activeTab === 'billing' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('billingSubscription')}</CardTitle>
                <CardDescription>{t('manageSubscription')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{t('proTrader')}</Badge>
                        <Badge variant="success">{t('active')}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{t('yourPlan')}</p>
                    </div>
                    <Button variant="outline" size="sm">{t('managePlan')}</Button>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">{t('paymentMethod')}</h4>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-6 rounded bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center">
                        <i className="fa-brands fa-cc-visa text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-mono">•••• •••• •••• 4242</p>
                        <p className="text-xs text-muted-foreground">{t('expires')} 12/26</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">{t('edit')}</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
